import React, { useMemo, useState, useEffect } from 'react';
import { Button, Typography, Divider, message } from 'antd';
import { FormikProvider, useFormik } from 'formik';
import { makeStyles } from '@material-ui/styles';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { CODE_TYPE, LOGIN_TYPE } from '~constants/auth';
import { phoneNumberJoin } from '~helpers/order';
import { validateTelephone, validateInternationalTelephone } from '~helpers/validate';
import countries from 'common/constants/countries';
import SvgFormat from 'common/helpers/svgFormat';
import event from '~helpers/mixpanel';
import useBoolean from 'common/hooks/useBoolean';
import { loginContentTypes } from '../login-content';
import {
  CAPTCHA_SIZE,
  getCaptchaModalErrorTip,
  VALIDATION_HEIGHT,
  VALIDATION_WIDTH,
} from '../captcha-modal';
import { FieldSelectPhoneNumber, getPhoneNumberModalErrorTip } from '../login-formik-fields';
import { MUTATION_GENERATE_CAPTCHA, MUTATION_SEND_PHONE_CODE } from '../queries';
import { useCheckLandlordExist } from '../useCheckLandlordExist';
import CaptchaModal from '../captcha-modal';
import { pageType } from '~constants/pages';

const useStyles = makeStyles({
  divider: {
    fontSize: '14px',
    fontWeight: 'normal !important',
    marginTop: '40px !important',
    marginBottom: '20px !important',
  },
  btnSvg: {
    height: 18,
    marginRight: 12,
    verticalAlign: 'text-bottom',
  },
  helperText: {
    fontSize: '12px',
    color: '#595959',
    marginTop: '8px !important',
  },
});

const LoginForSetPhoneNumber = ({
  handleNextContent,
  onChangeLoginType,
  clearLoginValue,
  codeType,
  setCodeType,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [captchaErrorTip, setCaptchaErrorTip] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [open, { setTrue: openModal, setFalse: closeModal }] = useBoolean();

  const [generateCaptcha, { data: captchaValidation, loading: generateCaptchaLoading }] =
    useMutation(MUTATION_GENERATE_CAPTCHA, {
      variables: {
        captchaSize: CAPTCHA_SIZE,
        validationWidth: VALIDATION_WIDTH,
        validationHeight: VALIDATION_HEIGHT,
      },
      onError: () => {
        message.error(t('sorry_an_error_occurred_please_try_again'));
      },
    });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        phone: yup
          .number()
          .label('Phone')
          .test('valid_number', t('please_enter_a_valid_phone_number'), value => {
            if (value) {
              return validateTelephone(value) && validateInternationalTelephone(value);
            }
            return true;
          })
          .required(t('this_part_cannot_be_empty')),
        phoneCode: yup.string().required(),
      }),
    [t],
  );

  const onCheckLandlordExistCompleted = e => {
    if (e?.checkLandlordExist?.exist) {
      setCodeType(CODE_TYPE.LOGIN);
      setShowWarning(!e.checkLandlordExist?.roles?.includes('LANDLORD'));
    } else {
      setCodeType(CODE_TYPE.SIGNUP);
      setShowWarning(true);
    }
    openModal();
    generateCaptcha();
  };

  const [queryCheckLandlordExist, { loading: checkLandlordExistLoading }] = useCheckLandlordExist({
    onCompleted: onCheckLandlordExistCompleted,
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    event('Clicked Phone Page', {
      'Clicked Position': 'Continue Btn',
      'Current Page Type': pageType.loginPage,
    });

    try {
      await queryCheckLandlordExist({
        variables: {
          type: LOGIN_TYPE.PHONE,
          value: phoneNumberJoin({
            phoneCode: values?.phoneCode,
            phoneNumber: values?.phone,
          }),
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const phoneCode = useMemo(() => {
    const countryCode = window.hurricane.clientInfo?.country?.toLowerCase();

    return countries.list.find(item => item.iso2Code === countryCode)?.phoneCode || '+86';
  }, []);

  const initialValues = useMemo(() => ({ phoneCode: phoneCode, phone: '' }), [phoneCode]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const phone = useMemo(
    () =>
      phoneNumberJoin({
        phoneCode: formik?.values?.phoneCode,
        phoneNumber: formik?.values?.phone,
      }),
    [formik],
  );

  const [mutationSendPhoneCode, { loading }] = useMutation(MUTATION_SEND_PHONE_CODE, {
    onCompleted: e => {
      if (e?.sendPhoneCode?.success) {
        handleNextContent(phone, loginContentTypes.SET_PHONE_NUMBER_CODE);
      } else {
        closeModal();
        message.error(t('sorry_an_error_occurred_please_try_again'));
      }
    },
    onError: e => {
      const errMassageText = e?.toString() ?? '';
      const captchaErrorTip = getCaptchaModalErrorTip(errMassageText);

      if (captchaErrorTip) {
        setCaptchaErrorTip(t(captchaErrorTip));
        generateCaptcha();
        return;
      }

      formik?.setSubmitting(false);
      closeModal();
      setCaptchaErrorTip();

      const PhoneNumberModalErrorTip = getPhoneNumberModalErrorTip(errMassageText);
      if (PhoneNumberModalErrorTip) {
        formik?.setErrors({ phone: t(PhoneNumberModalErrorTip) });
        return;
      }

      message.error(t('sorry_an_error_occurred_please_try_again'));
    },
  });

  const closeCaptchaModalAndCreateAccount = async (captchaCode, captchaId) => {
    if (captchaCode && captchaId) {
      await mutationSendPhoneCode({
        variables: {
          captchaCode,
          captchaId,
          phone,
          type: codeType,
        },
      });
    }
  };

  const handleToogleEmail = () => {
    event('Clicked Phone Page', {
      'Clicked Position': 'Email Method',
      'Current Page Type': pageType.loginPage,
    });

    clearLoginValue();
    onChangeLoginType(loginContentTypes.SET_EMAIL);
  };

  const handlePhoneNumberFocus = () => {
    event('Clicked Phone Page', {
      'Clicked Position': 'Phone Box',
      'Current Page Type': pageType.loginPage,
    });
  };

  const handleSelectFocus = () => {
    event('Clicked Phone Page', {
      'Clicked Position': 'Country Code',
      'Current Page Type': pageType.loginPage,
    });
  };

  useEffect(() => {
    event('Viewed Phone Page', { 'Current Page Type': pageType.loginPage });
  }, []);

  return (
    <>
      <FormikProvider value={formik}>
        <FieldSelectPhoneNumber
          fieldConfig="phone"
          phoneCodeFieldConfig={'phoneCode'}
          placeholder={t('phone_number')}
          style={{ marginTop: 40 }}
          onSelectFocus={handleSelectFocus}
          onFocus={handlePhoneNumberFocus}
        />
        <Typography className={classes.helperText}>
          {t('we_will_text_you_to_confirm_your_number_standard_message_and_data_rates_apply')}
        </Typography>
        <Button
          type="primary"
          size="large"
          onClick={formik.handleSubmit}
          loading={checkLandlordExistLoading}
        >
          {t('continue')}
        </Button>
      </FormikProvider>
      <Divider className={classes.divider}>{t('or')}</Divider>
      <Button
        size="large"
        style={{ border: '1px solid ', color: 'black', opacity: 0.85 }}
        onClick={handleToogleEmail}
      >
        <SvgFormat type="email-fill" className={classes.btnSvg} />
        {t('continue_with_email')}
      </Button>
      <Typography>
        {t('you_will_be_contacted_by_our_staff_to_agree_to_our_terms_and_conditions')}
      </Typography>
      {open && (
        <CaptchaModal
          authMethod={codeType === CODE_TYPE.LOGIN ? 'Phone Login' : 'Phone Signup'}
          captchaErrorTip={captchaErrorTip}
          captchaValidation={captchaValidation}
          closeCaptchaModalAndCreateAccount={closeCaptchaModalAndCreateAccount}
          generateCaptcha={generateCaptcha}
          generateCaptchaLoading={generateCaptchaLoading}
          onCloseCaptchaModal={closeModal}
          openCaptchaModal={open}
          setCaptchaErrorTip={setCaptchaErrorTip}
          isLoading={loading}
          showWarning={showWarning}
        />
      )}
    </>
  );
};

LoginForSetPhoneNumber.propTypes = {
  onChangeLoginType: PropTypes.func.isRequired,
  handleNextContent: PropTypes.func.isRequired,
  clearLoginValue: PropTypes.func.isRequired,
  codeType: PropTypes.string,
  setCodeType: PropTypes.func.isRequired,
};

export default LoginForSetPhoneNumber;
